import { useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useConfirm } from '@/hooks/useConfirm';
import { useNodes } from '@/hooks/data/useNodes';
import { Button } from '@/components/ui/Button';
import { PlusIcon } from '@/components/icons';
import { ErrorAlert, PageHeader } from '@/components/PageParts';
import { NodeModal } from '@/components/NodeModal';
import { NodesTable, type NodeRowAction } from '@/components/nodes/NodesTable';
import { PleskDomainsModal } from '@/components/nodes/PleskDomainsModal';
import { nodeHasCapability } from '@/config/nodeTypes';
import { pluralize } from '@/utils/format';
import type { Node } from '@/types/node';
import './Nodes.css';

/**
 * CDN Nodes page component
 *
 * Features:
 * - List all CDN nodes with status indicators
 * - Add, edit, delete nodes and rotate agent tokens (admin only)
 * - Health check nodes (operator+)
 * - Browse domains hosted on Plesk nodes
 */
export function Nodes() {
  const { can } = usePermissions();

  const {
    nodes,
    isLoading,
    error: loadError,
    reload,
    createNode,
    updateNode,
    deleteNode,
    checkHealth,
    rotateAgentToken,
    testPlesk,
  } = useNodes();

  // UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [pleskDomainsNode, setPleskDomainsNode] = useState<Node | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const healthCheck = useAsyncAction((node: Node) => checkHealth(node.id), {
    errorMessage: (node) => `Health check failed for ${node.name}`,
    getKey: (node) => node.id,
    onError: setActionError,
  });

  const openModal = (node: Node | null) => {
    setSelectedNode(node);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNode(null);
  };

  const getActions = (node: Node): Record<NodeRowAction, boolean> => ({
    pleskDomains: nodeHasCapability(node, 'hasPleskDomains'),
    health: can('nodes:health'),
    edit: can('nodes:update'),
    delete: can('nodes:delete'),
  });

  const handleRowAction = (action: NodeRowAction, node: Node) => {
    setActionError(null);
    switch (action) {
      case 'pleskDomains':
        setPleskDomainsNode(node);
        return;
      case 'health':
        void healthCheck.run(node);
        return;
      case 'edit':
        openModal(node);
        return;
      case 'delete':
        void confirm({
          title: 'Delete Node',
          message: (
            <>
              Are you sure you want to delete <strong>{node.name}</strong>? This action cannot be
              undone and will remove all associated configurations.
            </>
          ),
          confirmLabel: 'Delete',
          onConfirm: () => deleteNode(node.id),
          errorMessage: 'Failed to delete node',
        });
    }
  };

  const handleRetry = () => {
    setActionError(null);
    void reload();
  };

  const canCreate = can('nodes:create');

  return (
    <div className="nodes-page">
      <PageHeader
        classPrefix="nodes"
        title="CDN Nodes"
        count={pluralize(nodes.length, 'node')}
        actions={
          canCreate && (
            <Button leftIcon={<PlusIcon />} onClick={() => openModal(null)}>
              Add Node
            </Button>
          )
        }
      />

      <ErrorAlert classPrefix="nodes" message={actionError ?? loadError} onRetry={handleRetry} />

      <NodesTable
        nodes={nodes}
        isLoading={isLoading}
        onAddFirst={canCreate ? () => openModal(null) : undefined}
        getActions={getActions}
        isBusy={(_action, nodeId) => healthCheck.isPending(nodeId)}
        onAction={handleRowAction}
      />

      <NodeModal
        isOpen={isModalOpen}
        onClose={closeModal}
        node={selectedNode}
        onCreate={createNode}
        onUpdate={updateNode}
        onTestPlesk={testPlesk}
        onRotateAgentToken={can('nodes:rotate-token') ? rotateAgentToken : undefined}
      />

      <PleskDomainsModal
        node={pleskDomainsNode}
        onClose={() => setPleskDomainsNode(null)}
        canRefresh={can('nodes:refresh-plesk')}
      />

      {confirmDialog}
    </div>
  );
}
